import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../App';
import { NotionBlockRenderer } from '../components/shared';
import { Heart, ChevronDown, Sparkles, Target, Users, ShieldCheck, Star } from 'lucide-react';

var VALUE_ICONS = {
  'Compassion': Heart,
  'Dignity': Star,
  'Accountability': ShieldCheck,
  'Integrity': ShieldCheck,
  'Transparency': Sparkles,
  'Excellence': Target,
  'Respect': Users,
  'Safety': ShieldCheck,
  'Trust': Heart,
  'Clarity': Sparkles,
};

var VALUE_COLORS = [
  { bg: 'hsl(185 40% 95%)', border: 'hsl(185 55% 40%)', text: 'hsl(185 55% 22%)' },
  { bg: 'hsl(170 40% 95%)', border: 'hsl(170 45% 35%)', text: 'hsl(170 45% 20%)' },
  { bg: 'hsl(195 45% 95%)', border: 'hsl(195 50% 40%)', text: 'hsl(195 50% 22%)' },
  { bg: 'hsl(200 40% 95%)', border: 'hsl(200 50% 40%)', text: 'hsl(200 50% 22%)' },
  { bg: 'hsl(175 40% 95%)', border: 'hsl(175 45% 38%)', text: 'hsl(175 45% 20%)' },
  { bg: 'hsl(190 40% 95%)', border: 'hsl(190 50% 38%)', text: 'hsl(190 50% 22%)' },
  { bg: 'hsl(180 40% 95%)', border: 'hsl(180 45% 36%)', text: 'hsl(180 45% 20%)' },
  { bg: 'hsl(165 40% 95%)', border: 'hsl(165 45% 35%)', text: 'hsl(165 45% 20%)' },
];

export default function VisionValues() {
  var _useState1 = useState([]), visionSections = _useState1[0], setVisionSections = _useState1[1];
  var _useState2 = useState([]), values = _useState2[0], setValues = _useState2[1];
  var _useState3 = useState([]), introBlocks = _useState3[0], setIntroBlocks = _useState3[1];
  var _useState4 = useState(true), loading = _useState4[0], setLoading = _useState4[1];
  var _useState5 = useState(null), expandedValue = _useState5[0], setExpandedValue = _useState5[1];

  useEffect(function() {
    Promise.all([
      fetch(BACKEND_URL + '/api/pages/vision').then(function(r) { return r.json(); }),
      fetch(BACKEND_URL + '/api/pages/values').then(function(r) { return r.json(); }),
    ]).then(function(results) {
      var vision = results[0];
      var valuesData = results[1];
      setVisionSections(vision.sections || []);
      setValues(valuesData.values || []);
      setIntroBlocks(valuesData.intro_blocks || []);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, []);

  if (loading) {
    return React.createElement('div', { className: 'page-container' },
      React.createElement('div', { className: 'space-y-6' },
        Array.from({ length: 4 }).map(function(_, i) {
          return React.createElement('div', { key: i, className: 'h-24 bg-muted rounded-2xl animate-skeleton' });
        })
      )
    );
  }

  return React.createElement('div', { className: 'page-container', 'data-testid': 'vision-values' },
    
    // Hero
    React.createElement('div', {
      className: 'relative mb-12 rounded-2xl overflow-hidden',
      style: { background: 'radial-gradient(900px circle at 10% 0%, hsla(185,55%,38%,0.15), transparent 55%), radial-gradient(700px circle at 90% 10%, hsla(170,40%,40%,0.10), transparent 60%)' }
    },
      React.createElement('div', { className: 'px-8 py-14 sm:py-20 max-w-2xl' },
        React.createElement('div', { className: 'flex items-center gap-3 mb-5' },
          React.createElement(Heart, { className: 'text-primary', size: 28 }),
          React.createElement('span', { className: 'text-xs font-semibold uppercase tracking-widest text-primary' }, 'Pure Souls')
        ),
        React.createElement('h1', { className: 'text-3xl sm:text-4xl font-bold tracking-tight mb-3' }, 'Vision & Values'),
        React.createElement('p', { className: 'text-muted-foreground text-lg leading-relaxed' }, 'The principles that guide everything we do — from the first phone call to the final farewell.')
      )
    ),

    // Vision Sections — Quote-led
    React.createElement('div', { className: 'max-w-3xl mb-16' },
      React.createElement('h2', { className: 'text-xs font-bold uppercase tracking-widest text-primary mb-8' }, 'Our Vision & Direction'),
      
      visionSections.map(function(section, idx) {
        if (!section.title) return null;
        return React.createElement('div', { key: idx, className: 'mb-10' },
          // Quote pull
          section.quote ? React.createElement('blockquote', {
            className: 'text-xl sm:text-2xl font-semibold leading-relaxed mb-4',
            style: { color: 'hsl(var(--foreground))' }
          }, '"', section.quote, '"') : null,
          
          // Section title as subtle label
          React.createElement('p', {
            className: 'text-xs font-bold uppercase tracking-widest mb-3',
            style: { color: 'hsl(var(--primary))' }
          }, section.title),
          
          // Content blocks
          React.createElement('div', { className: 'text-muted-foreground leading-relaxed' },
            React.createElement(NotionBlockRenderer, { blocks: section.blocks })
          )
        );
      })
    ),

    // Values — Accordion Cards
    React.createElement('div', { className: 'max-w-3xl' },
      React.createElement('h2', { className: 'text-xs font-bold uppercase tracking-widest text-primary mb-3' }, 'Our Values & Standards'),
      
      // Intro text
      introBlocks.length > 0 ? React.createElement('div', { className: 'text-muted-foreground mb-8 text-lg leading-relaxed' },
        React.createElement(NotionBlockRenderer, { blocks: introBlocks })
      ) : null,
      
      // Value cards grid
      React.createElement('div', { className: 'space-y-3' },
        values.map(function(value, idx) {
          var isExpanded = expandedValue === idx;
          var colorScheme = VALUE_COLORS[idx % VALUE_COLORS.length];
          var IconComponent = null;
          
          // Find matching icon
          Object.keys(VALUE_ICONS).forEach(function(key) {
            if (value.name.toLowerCase().includes(key.toLowerCase())) {
              IconComponent = VALUE_ICONS[key];
            }
          });
          if (!IconComponent) IconComponent = Star;

          // Get the first action as a preview
          var preview = value.actions && value.actions.length > 0 ? value.actions[0] : '';
          
          return React.createElement('div', {
            key: idx,
            className: 'rounded-2xl overflow-hidden transition-all duration-200',
            style: { 
              border: '1px solid ' + (isExpanded ? colorScheme.border : 'hsl(var(--border))'),
              background: isExpanded ? colorScheme.bg : 'hsl(var(--card))',
            }
          },
            // Header (always visible)
            React.createElement('button', {
              onClick: function() { setExpandedValue(isExpanded ? null : idx); },
              className: 'w-full flex items-center gap-4 p-5 text-left transition-colors',
              'data-testid': 'value-card-' + idx
            },
              React.createElement('div', {
                className: 'w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0',
                style: { background: colorScheme.bg, border: '2px solid ' + colorScheme.border }
              },
                React.createElement(IconComponent, { size: 20, style: { color: colorScheme.border } })
              ),
              React.createElement('div', { className: 'flex-1 min-w-0' },
                React.createElement('h3', { className: 'text-base font-bold', style: { color: isExpanded ? colorScheme.text : 'hsl(var(--foreground))' } }, value.name),
                !isExpanded && preview ? React.createElement('p', { className: 'text-xs text-muted-foreground mt-0.5 truncate' }, preview) : null
              ),
              React.createElement(ChevronDown, {
                size: 18,
                className: 'text-muted-foreground transition-transform duration-200 flex-shrink-0',
                style: { transform: isExpanded ? 'rotate(180deg)' : 'none' }
              })
            ),
            
            // Expanded content — grouped by sub-sections
            isExpanded ? React.createElement('div', { className: 'px-5 pb-5' },
              value.sections && value.sections.length > 0 ? React.createElement('div', { className: 'space-y-4' },
                value.sections.map(function(section, si) {
                  var isNot = section.title.toLowerCase().indexOf('not') >= 0;
                  var isWhere = section.title.toLowerCase().indexOf('where') >= 0;
                  return React.createElement('div', { key: si },
                    React.createElement('p', { 
                      className: 'text-xs font-bold uppercase tracking-wider mb-2',
                      style: { color: isNot ? 'hsl(0, 50%, 45%)' : isWhere ? 'hsl(var(--muted-foreground))' : colorScheme.border }
                    }, section.title),
                    React.createElement('div', { className: 'space-y-1.5' },
                      section.items.map(function(item, ii) {
                        return React.createElement('div', { key: ii, className: 'flex items-start gap-3' },
                          React.createElement('div', {
                            className: 'w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0',
                            style: { background: isNot ? 'hsl(0, 50%, 60%)' : colorScheme.border }
                          }),
                          React.createElement('p', { 
                            className: 'text-sm leading-relaxed', 
                            style: { color: isNot ? 'hsl(0, 40%, 40%)' : colorScheme.text } 
                          }, item)
                        );
                      })
                    )
                  );
                })
              ) : value.actions && value.actions.length > 0 ? React.createElement('div', { className: 'space-y-2' },
                value.actions.map(function(action, ai) {
                  return React.createElement('div', { key: ai, className: 'flex items-start gap-3' },
                    React.createElement('div', {
                      className: 'w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0',
                      style: { background: colorScheme.border }
                    }),
                    React.createElement('p', { className: 'text-sm leading-relaxed', style: { color: colorScheme.text } }, action)
                  );
                })
              ) : React.createElement('div', { className: 'text-sm leading-relaxed', style: { color: colorScheme.text } },
                React.createElement(NotionBlockRenderer, { blocks: value.blocks })
              )
            ) : null
          );
        })
      )
    )
  );
}
